import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { OrganizationOverrides } from '@dadohr/shared/domainTypes/org';
import { Button, TextField } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { ImageUploadDialog } from '../../../../../../../components/ImageUpload';
import { storeImage } from '../../../../../../../features/Org/pages/Branding';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../../../hooks/useSnackbar';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { Section } from '../../../../../../../layout/Section';
import { updateDoc } from '../../../../../../../services/firebase';

export const Overrides: React.FC<{
  tpl: Doc<IExperienceTemplate>;
}> = ({ tpl }) => {
  const { enqueueSnackbarSuccess, enqueueSnackbarWarning } = useSnackbar();
  const imageUploadDialog = useDialogState();
  const [model, setModel] = useState(
    JSON.stringify(tpl.data.overrides?.org || {}, null, 2)
  );
  const [err, setErr] = useState('');
  const onSave = async () => {
    setErr('');
    if (model === '') {
      // set to null
      await updateDoc(tpl, () => ({ overrides: null }));
      return;
    }
    try {
      const nextData: OrganizationOverrides = JSON.parse(model);
      if (nextData.name !== undefined) {
        if (!nextData.name) {
          setErr('Name must not be empty');
          return;
        }
        if (nextData.logoUrl !== undefined) {
          if (!nextData.logoUrl) {
            setErr('Logo URL must not be empty');
            return;
          }
        }
        if (
          nextData.experienceTheming !== undefined &&
          nextData.experienceTheming !== null
        ) {
          if (
            !nextData.experienceTheming?.colors ||
            !nextData.experienceTheming?.colors?.primary ||
            !nextData.experienceTheming?.colors?.primary?.light ||
            !nextData.experienceTheming?.colors?.primary?.main ||
            !nextData.experienceTheming?.colors?.primary?.dark ||
            !nextData.experienceTheming?.colors?.primary?.contrastText
          ) {
            setErr('Theme incomplete');
            return;
          }
        }
      }

      await updateDoc(tpl, () => ({ overrides: { org: nextData } }));
      enqueueSnackbarSuccess('Update successful');
    } catch (err) {
      console.error(err);
      setErr('Cannot parse JSON');
    }
  };

  const currentLogoUrl = useMemo(() => {
    try {
      const data: OrganizationOverrides = JSON.parse(model);
      return data.logoUrl || '';
    } catch {
      return '';
    }
  }, [model]);
  return (
    <Section>
      <CanvasBar>
        <div>Overrides (used for custom branding etc...)</div>
        <Button
          variant="outlined"
          color="primary"
          onClick={imageUploadDialog.openDialog}
          size="small"
        >
          Upload Logo
        </Button>
        <ButtonWithPromise
          disabled={
            model === JSON.stringify(tpl.data.overrides?.org || {}, null, 2)
          }
          pending="Saving..."
          onClick={onSave}
          variant="contained"
          color="primary"
          size="small"
        >
          Save changes
        </ButtonWithPromise>
      </CanvasBar>
      <FlexContainer fullWidth>
        <TextField
          multiline
          fullWidth
          rows={20}
          value={model}
          onChange={(ev) => setModel(ev.target.value)}
          error={!!err}
          helperText={err || ''}
          spellCheck={false}
          inputProps={{
            style: {
              fontFamily: 'monospace'
            }
          }}
        />
      </FlexContainer>

      <ImageUploadDialog
        title="Upload logo"
        open={imageUploadDialog.dialogOpen}
        onCancel={imageUploadDialog.closeDialog}
        src={currentLogoUrl}
        onUpload={async (blob) => {
          const logoUrl = await storeImage(blob);
          enqueueSnackbarSuccess(`Logo uploaded: ${logoUrl}`);
          try {
            const data: OrganizationOverrides = JSON.parse(model) || '{}';
            data.logoUrl = logoUrl;
            setModel(JSON.stringify(data, null, 2));
          } catch {
            enqueueSnackbarWarning(
              'Unable to update JSON - please add logo url manually'
            );
          }
        }}
      />
    </Section>
  );
};
